import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPartner, updatePartner } from '../../services/api';
import HelpIcon from '../help/HelpIcon';
import {
  CM_TIERS,
  PARTNER_STATUSES,
  PROGRAMS,
  TIERS,
  buildPartnerPayload,
  partnerToForm,
  validatePartnerForm,
} from '../../utils/partnersAdmin';

/** Texto informativo con los escalones del programa CM (RES-98). */
export function CmTiersInfo() {
  const { t } = useTranslation();
  return (
    <div className="cm-tiers-info" data-testid="cm-tiers-info">
      <p>
        {t(
          'partners.program.cmInfo',
          'Cobra mientras el local pague, sin tope de meses. El porcentaje depende de los locales activos pagos y se aplica a toda la cartera:'
        )}
      </p>
      <ul>
        {CM_TIERS.map((tier) => (
          <li key={tier.minLocations}>
            {tier.maxLocations
              ? t('partners.program.cmTierRange', '{{min}} a {{max}} locales: {{pct}}%', {
                  min: tier.minLocations,
                  max: tier.maxLocations,
                  pct: tier.pct,
                })
              : t('partners.program.cmTierOpen', '{{min}} o más locales: {{pct}}%', {
                  min: tier.minLocations,
                  pct: tier.pct,
                })}
          </li>
        ))}
      </ul>
      <p className="field-hint">
        {t('partners.program.cmNoBonus', 'Sin bono por alta. Los meses de comisión no aplican.')}
      </p>
    </div>
  );
}

/**
 * Alta / edición de un código de partner. Al crear manda `program`; al editar, el payload
 * incluye `program` (cambiarlo aplica desde el próximo cálculo).
 */
function PartnerFormModal({ partner, onClose, onSaved }) {
  const { t } = useTranslation();
  const editing = !!partner;
  const [form, setForm] = useState(() => partnerToForm(partner));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const isCm = form.program === 'cm';

  const setField = (field, value) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const errorText = (field) => {
    if (!errors[field]) return null;
    if (field === 'code') return t('partners.validation.code', 'Código: 4-20 alfanuméricos');
    if (field === 'partnerName') return t('partners.validation.name', 'Requerido');
    return t('partners.validation.email', 'Email inválido');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validatePartnerForm(form, { editing });
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSaving(true);
    setSubmitError(null);
    try {
      const payload = buildPartnerPayload(form, { editing });
      if (editing) {
        await updatePartner(partner.code, payload);
      } else {
        await createPartner(payload);
      }
      await onSaved();
    } catch (error) {
      if (error?.response?.status === 409) {
        setSubmitError(t('partners.duplicateError', 'Ya existe un partner con ese código'));
      } else {
        console.error('Error saving partner:', error);
        setSubmitError(t('partners.saveError', 'Error al guardar el partner'));
      }
    } finally {
      setSaving(false);
    }
  };

  const numberField = (field, labelKey, labelDefault, placeholder) => (
    <div className="form-group">
      <label htmlFor={`partner-${field}`}>{t(labelKey, labelDefault)}</label>
      <input
        id={`partner-${field}`}
        name={field}
        type="number"
        min="0"
        value={form[field]}
        placeholder={placeholder}
        onChange={(e) => setField(field, e.target.value)}
      />
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content partners-form-modal">
        <div className="modal-header">
          <h2>
            {editing
              ? t('partners.editPartner', 'Editar partner')
              : t('partners.newPartner', 'Nuevo código de partner')}
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.close', 'Cerrar')}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>
            {submitError && (
              <div className="alert alert-error" role="alert" data-testid="partner-form-error">
                {submitError}
              </div>
            )}
            <div className={`form-group${errors.code ? ' has-error' : ''}`}>
              <label htmlFor="partner-code">{t('partners.code', 'Código')} *</label>
              <input
                id="partner-code"
                name="code"
                type="text"
                value={form.code}
                disabled={editing}
                maxLength={20}
                placeholder="NARDA20"
                aria-required="true"
                aria-invalid={!!errors.code}
                onChange={(e) => setField('code', e.target.value.toUpperCase())}
              />
              {editing && (
                <span className="field-hint">
                  {t('partners.codeImmutable', 'El código no se puede modificar')}
                </span>
              )}
              {errors.code && (
                <span className="field-error" role="alert">
                  {errorText('code')}
                </span>
              )}
            </div>
            <div className="form-row">
              <div className={`form-group${errors.partnerName ? ' has-error' : ''}`}>
                <label htmlFor="partner-name">{t('partners.name', 'Nombre')} *</label>
                <input
                  id="partner-name"
                  name="partnerName"
                  type="text"
                  value={form.partnerName}
                  aria-required="true"
                  aria-invalid={!!errors.partnerName}
                  onChange={(e) => setField('partnerName', e.target.value)}
                />
                {errors.partnerName && (
                  <span className="field-error" role="alert">
                    {errorText('partnerName')}
                  </span>
                )}
              </div>
              <div className={`form-group${errors.partnerEmail ? ' has-error' : ''}`}>
                <label htmlFor="partner-email">{t('partners.email', 'Email')} *</label>
                <input
                  id="partner-email"
                  name="partnerEmail"
                  type="email"
                  value={form.partnerEmail}
                  aria-required="true"
                  aria-invalid={!!errors.partnerEmail}
                  onChange={(e) => setField('partnerEmail', e.target.value)}
                />
                {errors.partnerEmail && (
                  <span className="field-error" role="alert">
                    {errorText('partnerEmail')}
                  </span>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="label-with-help">
                  <label htmlFor="partner-program">
                    {t('partners.program.label', 'Programa')} *
                  </label>
                  <HelpIcon topicKey="partners.programs" />
                </div>
                <select
                  id="partner-program"
                  name="program"
                  value={form.program}
                  onChange={(e) => setField('program', e.target.value)}
                >
                  {PROGRAMS.map((p) => (
                    <option key={p} value={p}>
                      {p === 'cm'
                        ? t('partners.program.cm', 'Community manager o agencia')
                        : t('partners.program.influencer', 'Influencer')}
                    </option>
                  ))}
                </select>
                {editing && partner.program && form.program !== partner.program && (
                  <span className="field-hint">
                    {t(
                      'partners.program.changeHint',
                      'El cambio de programa aplica desde el próximo cálculo de comisiones.'
                    )}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="partner-tier">{t('partners.tier', 'Tier')} *</label>
                <select
                  id="partner-tier"
                  name="tier"
                  value={form.tier}
                  onChange={(e) => setField('tier', e.target.value)}
                >
                  {TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {tier}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {editing && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="partner-status">{t('partners.statusLabel', 'Estado')}</label>
                  <select
                    id="partner-status"
                    name="status"
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value)}
                  >
                    {PARTNER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`partners.status.${s}`, s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {isCm ? (
              <CmTiersInfo />
            ) : (
              <>
                <div className="form-row">
                  {numberField('commissionPct', 'partners.commissionPct', 'Comisión %', '20')}
                  {numberField(
                    'commissionMonths',
                    'partners.commissionMonths',
                    'Meses de comisión',
                    '12'
                  )}
                </div>
                <div className="form-row">
                  {numberField(
                    'signOnBonus',
                    'partners.signOnBonus',
                    'Sign-on bonus (ARS)',
                    form.tier === 'A' ? '50000' : '0'
                  )}
                </div>
              </>
            )}
            <div className="form-row">
              {numberField(
                'enterpriseFlat',
                'partners.enterpriseFlat',
                'Flat Enterprise (ARS)',
                '500000'
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="partner-contractStart">
                  {t('partners.contractStart', 'Inicio de contrato')}
                </label>
                <input
                  id="partner-contractStart"
                  type="date"
                  value={form.contractStart}
                  onChange={(e) => setField('contractStart', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="partner-contractEnd">
                  {t('partners.contractEnd', 'Fin de contrato')}
                </label>
                <input
                  id="partner-contractEnd"
                  type="date"
                  value={form.contractEnd}
                  onChange={(e) => setField('contractEnd', e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PartnerFormModal;
